import { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useAppDispatch, useAppSelector } from '@/app/reduxHooks'
import { popOverlay, type LogsOverlayData } from '@/features/adp/state/overlaySlice'
import { usePipelineLogsQuery } from '@/features/adp/queries/usePipelineQueries'
import type { PipelineLogsMode } from '@/features/adp/services/pipeline/pipeline.types'
import { cn } from '@/shared/lib/utils'
import { formatDateTime } from '@/shared/utils/formatDate'
import { Button } from '@/ui/button'
import { Dialog, DialogContent, DialogTitle } from '@/ui/dialog'
import { MaterialIcon } from '@/ui/material-icon'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/ui/select'
import styles from './LogsModal.module.scss'

const MODE_OPTIONS: ReadonlyArray<{ value: PipelineLogsMode; label: string }> = [
  { value: 'short', label: 'Short' },
  { value: 'full', label: 'Full' },
]

const DEFAULT_MODE: PipelineLogsMode = 'short'

const LogsModal = () => {
  const dispatch = useAppDispatch()
  const { isOpen, type, data } = useAppSelector((state) => state.overlay)

  const isVisible = isOpen && type === 'logs-modal'
  const modalData: LogsOverlayData | null =
    isVisible && data && 'runId' in data ? (data as LogsOverlayData) : null

  const dataFlowId = modalData?.dataFlowId ?? 0
  const runId = modalData?.runId ?? ''
  const pipelineName = modalData?.pipelineName ?? ''
  const startTime = modalData?.startTime ?? null
  const endTime = modalData?.endTime ?? null

  const runRangeLabel = useMemo(() => {
    const start = startTime ? formatDateTime(startTime) : null
    const end = endTime ? formatDateTime(endTime) : null
    if (start && end) return `${start} → ${end}`
    if (start) return start
    return null
  }, [startTime, endTime])

  const [mode, setMode] = useState<PipelineLogsMode>(DEFAULT_MODE)

  // Reset mode whenever a new run is opened so the dropdown lands on the default.
  useEffect(() => {
    if (isVisible) setMode(DEFAULT_MODE)
  }, [isVisible, runId])

  const { data: logsResponse, isLoading, isError, error } = usePipelineLogsQuery(
    { data_flow_id: dataFlowId, run_id: runId, mode },
    { enabled: isVisible && Boolean(dataFlowId && runId) }
  )

  const lines = useMemo(() => logsResponse?.logs ?? [], [logsResponse?.logs])

  const bodyRef = useRef<HTMLDivElement | null>(null)
  const [isCopying, setIsCopying] = useState(false)

  // Logs is opened nested on top of Run History: popOverlay returns the user
  // to the History modal underneath. closeOverlay would tear down both.
  const handleClose = () => dispatch(popOverlay())

  const handleJumpToEnd = () => {
    const el = bodyRef.current
    if (el) el.scrollTop = el.scrollHeight
  }

  const handleCopy = async () => {
    if (lines.length === 0) return
    setIsCopying(true)
    try {
      await navigator.clipboard.writeText(lines.join('\n'))
      toast.success('Logs copied to clipboard')
    } catch {
      toast.error('Failed to copy logs')
    } finally {
      setIsCopying(false)
    }
  }

  const handleDownload = () => {
    if (lines.length === 0) return
    const safeName = (pipelineName || 'pipeline').replace(/[^a-z0-9_-]+/gi, '_')
    const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${safeName}-${mode}-logs.txt`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  let body: React.ReactNode
  if (isLoading) {
    body = <div className={styles.statusOverlay}>Loading logs…</div>
  } else if (isError) {
    body = (
      <div className={`${styles.statusOverlay} ${styles.statusError}`}>
        {error?.message || 'Failed to load logs.'}
      </div>
    )
  } else if (lines.length === 0) {
    body = (
      <div className={styles.statusOverlay}>No log entries available for this run.</div>
    )
  } else {
    body = (
      <pre className={styles.logPre}>
        {lines.map((line, idx) => (
          <div key={idx} className={styles.logRow}>
            <span className={styles.lineNo}>{idx + 1}</span>
            <span className={styles.lineText}>{line}</span>
          </div>
        ))}
      </pre>
    )
  }

  const toolbarDisabled = lines.length === 0

  return (
    <Dialog
      open={isVisible}
      onOpenChange={(open) => {
        if (!open) handleClose()
      }}
    >
      <DialogContent
        className={cn(
          'max-w-[960px] w-[95vw] h-[78vh] max-h-[78vh] p-0 flex flex-col',
          styles.dialogContent
        )}
      >
        <div className={styles.header}>
          <div className={styles.titleBlock}>
            <DialogTitle className={styles.title}>Logs</DialogTitle>
            {runRangeLabel && (
              <p className={styles.subtitle}>{runRangeLabel}</p>
            )}
          </div>

          <div className={styles.toolbar}>
            <Select
              value={mode}
              onValueChange={(value) => setMode(value as PipelineLogsMode)}
            >
              <SelectTrigger className={styles.modeSelect} aria-label="Log mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MODE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleJumpToEnd}
              disabled={toolbarDisabled}
              className={styles.jumpBtn}
            >
              Jump to End
            </Button>

            <button
              type="button"
              className={styles.iconBtn}
              onClick={handleCopy}
              disabled={toolbarDisabled || isCopying}
              aria-label="Copy logs"
              title="Copy"
            >
              <MaterialIcon name="content_copy" size={18} />
            </button>

            <button
              type="button"
              className={styles.iconBtn}
              onClick={handleDownload}
              disabled={toolbarDisabled}
              aria-label="Download logs"
              title="Download"
            >
              <MaterialIcon name="download" size={18} />
            </button>

            <button
              type="button"
              className={styles.iconBtn}
              onClick={handleClose}
              aria-label="Close logs"
              title="Close"
            >
              <MaterialIcon name="close" size={20} />
            </button>
          </div>
        </div>

        <div ref={bodyRef} className={styles.body}>
          {body}
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default LogsModal
